import React, { useEffect, useState } from 'react'
import { Form } from 'antd'
import TestInputs from './form-items/TestInputs'
import { actions as unitTestsActions } from '../../../../../../../store/reducers/unit-tests.reducer'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import TestName from './form-items/TestName'
import TestInput from './form-items/TestInput'
import TestDatabaseDump from './form-items/TestDatabaseDump'
import TestFunctionName from './form-items/TestFunctionName'
import TestFramework from './form-items/TestFramework'
import TestTestSuite from './form-items/TestTestSuite'
import TestMainClassName from './form-items/TestMainClassName'
import TestCpuTimeLimit from './form-items/TestCpuTimeLimit'
import TestExactMatch from './form-items/TestExactMatch'
import TestMemoryLimit from './form-items/TestMemoryLimit'
import TestIgnoreCase from './form-items/TestIgnoreCase'
import TestFixtureFiles from './form-items/TestFixtureFiles'
import { LANGUAGES } from '../../../../../../../constants/languages'
import { useAutoFocusFirstInput } from '../../../../../../../../hooks/useAutoFocusFirstInput'

const TestForm = ({
    submitTest,
    onSuccessSubmit,
    setIsWaitingForSubmitTest,
    fields,
    setFramework,
    isWaitingForFixtureFilesUpload,
    setIsWaitingForFixtureFilesUpload,
}) => {
    const language = useSelector(state => state.assignment.assignment.language)
    const dispatch = useDispatch()
    const editingTest = useSelector(state =>
        state.unitTests.unitTests.find(unitTest => unitTest.id === state.unitTests.currentlyEditingTestId),
    )

    const { token } = useParams()
    const [form] = Form.useForm()
    const testSuite = Form.useWatch('testSuite', form)
    useAutoFocusFirstInput()

    const [fixtureFilesPath, setFixtureFilesPath] = useState('')

    useEffect(() => {
        if (editingTest) {
            form.setFieldsValue({
                name: editingTest.scenario_name,
                input: editingTest.test_data.input,
                inputs: editingTest.test_data.input,
                functionName: editingTest.test_data.function_name,
                framework: editingTest.test_data.framework,
                testSuite: editingTest.test_data.test_suite,
                mainClassName: editingTest.test_data.main_class_name,
                cpuTimeLimit: editingTest.cpu_limit,
                memoryLimit: editingTest.memory_limit,
                exactMatch: editingTest.test_data.exact_match,
                ignoreCase: editingTest.test_data.ignore_case,
                fixtureFilesPath: editingTest.test_data.additional_files_path,
            })
            if (editingTest.test_data.framework) setFramework(editingTest.test_data.framework)
            if (editingTest.test_data.additional_files_path)
                setFixtureFilesPath(editingTest.test_data.additional_files_path)
        }
    }, [form, editingTest])

    useEffect(() => () => dispatch(unitTestsActions.setCurrentlyEditingTestId(null)), [])

    useEffect(() => {
        // according to SN-2137 we want to synchronize mainClassName field with testSuite
        if (language === LANGUAGES.JAVA.value) {
            const matches = testSuite?.match(/public class (\w+)Test\b/)
            if (matches && matches[1]) form.setFieldValue('mainClassName', matches[1])
        }
    }, [testSuite])

    const handleSubmitTest = async fields => {
        if (fixtureFilesPath) fields.fixtureFilesPath = fixtureFilesPath
        setIsWaitingForSubmitTest(true)
        const createdTest = await submitTest(token, fields)
        setIsWaitingForSubmitTest(false)
        if (createdTest) onSuccessSubmit()
    }

    const FormContent = () => (
        <>
            {fields.name && <TestName initialValue={editingTest?.scenario_name} />}
            {fields.databaseDump && <TestDatabaseDump />}
            {fields.input && <TestInput initialValue={editingTest?.test_data.input} />}
            {fields.functionName && <TestFunctionName initialValue={editingTest?.test_data.function_name} />}
            {fields.framework && (
                <TestFramework setFramework={setFramework} initialValue={editingTest?.test_data.framework} />
            )}
            {fields.inputs && <TestInputs initialValue={editingTest?.test_data.input} />}
            {fields.mainClassName && <TestMainClassName initialValue={editingTest?.test_data.main_class_name} />}
            <div className='d-flex-space-between'>
                {fields.cpuTimeLimit && <TestCpuTimeLimit initialValue={editingTest?.cpu_limit} />}
                {fields.exactMatch && <TestExactMatch />}
            </div>
            <div className='d-flex-space-between'>
                {fields.memoryLimit && <TestMemoryLimit initialValue={editingTest?.memory_limit} />}
                {fields.ignoreCase && <TestIgnoreCase initialValue={editingTest?.ignore_case} />}
            </div>
            {fields.fixtureFiles && (
                <TestFixtureFiles
                    fixtureFilesPath={fixtureFilesPath}
                    setFixtureFilesPath={setFixtureFilesPath}
                    isWaitingForFixtureFilesUpload={isWaitingForFixtureFilesUpload}
                    setIsWaitingForFixtureFilesUpload={setIsWaitingForFixtureFilesUpload}
                />
            )}
        </>
    )

    return (
        <Form
            form={form}
            name='test-form'
            onFinish={handleSubmitTest}
            layout='vertical'
            requiredMark={false}
            validateTrigger={['onBlur', 'onSubmit']}
        >
            {fields.testSuite ? (
                <div className='component-with-editor__container'>
                    <div className='component-with-editor__content'>
                        <FormContent />
                    </div>
                    <div className='component-with-editor__editor'>
                        <TestTestSuite />
                    </div>
                </div>
            ) : (
                <FormContent />
            )}
        </Form>
    )
}

export default TestForm
